// src/components/texting/TextingCenter.jsx
// The Texting Center (spec 2026-07-19): the approval queue for every
// suggested text (Today's Texts) + a team-wide inbox of all conversations.
// Nothing sends without a human tap. Team Lead + Client Care only.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, SkipForward } from "lucide-react";
import { toast } from "react-toastify";
import { collection, onSnapshot } from "firebase/firestore";
import { firestored } from "../../firebase/firebaseConfig";
import { useStateValue } from "../../context/StateProvider";
import {
  listenBuyerLeads,
  getAllContactsFromFirebase,
} from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import {
  canUseTexting,
  canUseAi,
  getTextingSettings,
  sendText,
  contactPhoneOf,
  normalizePhoneE164,
  useUnreadTexts,
} from "../../utils/texting";
import NewTextModal from "./NewTextModal";
import {
  syncTextJobs,
  useSuggestedTextJobs,
  resolveTextJob,
} from "../../utils/textJobs";
import TextThread from "./TextThread";
import GroupTextModal from "./GroupTextModal";
import { useActionDialog } from "../ActionDialog";

const RULE_LABELS = {
  drip: { label: "DRIP", cls: "bg-slate-50 text-linkColor" },
  apptReminder: { label: "REMINDER", cls: "bg-amber-50 text-amber-700" },
  closingReminder: { label: "CLOSING", cls: "bg-amber-50 text-amber-700" },
  reviewRequest: { label: "REVIEW ASK", cls: "bg-pink-50 text-pink-700" },
  anniversary: { label: "ANNIVERSARY", cls: "bg-pink-50 text-pink-700" },
  birthday: { label: "BIRTHDAY", cls: "bg-pink-50 text-pink-700" },
  aiReply: { label: "✨ AI REPLY", cls: "bg-emerald-50 text-emerald-700" },
};

const fmtNum = (e164) => {
  const d = String(e164 || "").replace(/\D/g, "");
  return d.length === 11
    ? `(${d.slice(1, 4)}) ${d.slice(4, 7)}-${d.slice(7)}`
    : e164 || "";
};
const fmtWhen = (ms) =>
  ms
    ? new Date(ms).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "";

export default function TextingCenter() {
  const [{ user, deals }] = useStateValue();
  const { confirm, dialog } = useActionDialog();
  const allowed = canUseTexting(user);

  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState(null);
  const [messages, setMessages] = useState([]);
  const [drafts, setDrafts] = useState({}); // jobId -> edited body
  const [busy, setBusy] = useState({}); // jobId -> true while sending
  const [selectedKey, setSelectedKey] = useState("");
  const [search, setSearch] = useState("");
  const [groupOpen, setGroupOpen] = useState(false);
  const [newTextOpen, setNewTextOpen] = useState(false);
  const [draftConvo, setDraftConvo] = useState(null); // "New text" target
  const [inboxFilter, setInboxFilter] = useState("all"); // all | unread
  const [pins, setPins] = useState({});
  const syncedRef = useRef(false);

  // 📌 pinned conversations, remembered per person on this device.
  useEffect(() => {
    try {
      setPins(JSON.parse(window.localStorage.getItem(`tesPins:${user?.id || "anon"}`) || "{}"));
    } catch (e) {
      setPins({});
    }
  }, [user?.id]);
  const togglePin = (key) => {
    setPins((p) => {
      const next = { ...p };
      if (next[key]) delete next[key];
      else next[key] = true;
      try {
        window.localStorage.setItem(`tesPins:${user?.id || "anon"}`, JSON.stringify(next));
      } catch (e) {
        // private mode: pins just won't persist
      }
      return next;
    });
  };

  const jobs = useSuggestedTextJobs(allowed);
  const unread = useUnreadTexts(allowed);

  // Avatar initials. A conversation with no matched contact falls back to a
  // formatted phone number, which has no letters — taking the first character
  // of each "word" there produced junk like "(4". Use letters only.
  const avatarInitials = (name) => {
    const letters = String(name || "")
      .split(/\s+/)
      .map((w) => (w.match(/[A-Za-z]/) || [])[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
    return letters || "?";
  };

  // Names for conversations the pipeline doesn't know: look the number up in
  // the user's synced Contacts, so "(612) 309-7581" becomes a real person.
  const [contactNameByPhone, setContactNameByPhone] = useState({});
  const [contactList, setContactList] = useState([]); // for "New text"
  useEffect(() => {
    if (!allowed || !user?.id) return;
    getAllContactsFromFirebase(user.id)
      .then((all) => {
        const map = {};
        const list = [];
        (all || []).forEach((raw) => {
          const c = normalizeContact(raw);
          const p = normalizePhoneE164(c?.phone || "");
          if (!p || !c?.name) return;
          if (!map[p]) map[p] = c.name;
          list.push({ name: c.name, phone: p });
        });
        setContactNameByPhone(map);
        setContactList(list);
      })
      .catch((e) => console.error("contact name lookup failed:", e));
  }, [allowed, user?.id]);

  useEffect(() => {
    if (!allowed) return undefined;
    return listenBuyerLeads((all) => setLeads(all || []));
  }, [allowed]);

  useEffect(() => {
    if (allowed) getTextingSettings().then(setSettings);
  }, [allowed]);

  // Materialize/cancel suggestions once per visit, after data arrives.
  useEffect(() => {
    if (!allowed || syncedRef.current || !settings || leads.length === 0) return;
    syncedRef.current = true;
    syncTextJobs({ leads, deals: deals || [], settings }).then((r) => {
      if (r.created || r.cancelled) {
        console.info(`textJobs sync: +${r.created} / -${r.cancelled}`);
      }
    });
  }, [allowed, settings, leads, deals]);

  // Team-wide inbox: every text message, grouped into conversations.
  useEffect(() => {
    if (!allowed) return undefined;
    return onSnapshot(
      collection(firestored, "textMessages"),
      (snap) => setMessages(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("inbox listener failed:", err),
    );
  }, [allowed]);

  const leadsById = useMemo(() => {
    const m = {};
    leads.forEach((l) => {
      if (l?.id) m[l.id] = l;
    });
    return m;
  }, [leads]);

  const dueJobs = useMemo(() => {
    const now = Date.now();
    return jobs.filter((j) => (j.dueAt || 0) <= now);
  }, [jobs]);

  const ruleCounts = {};
  (canUseAi(user) ? dueJobs : dueJobs.filter((j) => j.ruleKey !== "aiReply")
  ).forEach((j) => {
    ruleCounts[j.ruleKey] = (ruleCounts[j.ruleKey] || 0) + 1;
  });

  const convos = useMemo(() => {
    const byKey = {};
    messages.forEach((m) => {
      const key = m.leadId
        ? `lead:${m.leadId}`
        : m.dealId
          ? `deal:${m.dealId}`
          : `num:${m.direction === "inbound" ? m.fromNumber : m.toNumber}`;
      const number = m.direction === "inbound" ? m.fromNumber : m.toNumber;
      const cur = byKey[key] || {
        key,
        leadId: m.leadId || "",
        dealId: m.dealId || "",
        number: "",
        name: "",
        lastBody: "",
        lastAt: 0,
      };
      if (number) cur.number = number;
      if (m.contactName) cur.name = m.contactName;
      if ((m.createdAt || 0) >= cur.lastAt) {
        cur.lastAt = m.createdAt || 0;
        cur.lastBody = m.body || "";
      }
      byKey[key] = cur;
    });
    const q = search.trim().toLowerCase();
    return Object.values(byKey)
      .map((c) => ({
        ...c,
        name:
          c.name ||
          contactNameByPhone[normalizePhoneE164(c.number)] ||
          fmtNum(c.number) ||
          "Unknown",
        unread: c.leadId
          ? unread.leads[c.leadId] || 0
          : c.dealId
            ? unread.deals[c.dealId] || 0
            : 0,
      }))
      .filter(
        (c) =>
          !q ||
          c.name.toLowerCase().includes(q) ||
          String(c.number || "").includes(q.replace(/\D/g, "") || q),
      )
      .filter((c) => inboxFilter !== "unread" || c.unread > 0)
      .sort(
        (a, b) =>
          (pins[b.key] ? 1 : 0) - (pins[a.key] ? 1 : 0) || b.lastAt - a.lastAt,
      );
  }, [messages, unread, search, contactNameByPhone, inboxFilter, pins]);

  const selected =
    convos.find((c) => c.key === selectedKey) ||
    (draftConvo && draftConvo.key === selectedKey ? draftConvo : null) ||
    convos[0];

  // Only Team Leads see AI-drafted queue items (Henry's rule: NO agents,
  // and Client Care texts without the AI).
  const dueJobsVisible = canUseAi(user)
    ? dueJobs
    : dueJobs.filter((j) => j.ruleKey !== "aiReply");

  const sendJob = async (job) => {
    if (busy[job.id]) return { ok: false };
    setBusy((b) => ({ ...b, [job.id]: true }));
    try {
      const phone = await contactPhoneOf(job.contactId);
      if (!phone) {
        toast.error(`${job.contactName || "This lead"} has no mobile number on file.`);
        return { ok: false };
      }
      const body = (drafts[job.id] ?? job.body).trim();
      if (!body) return { ok: false };
      const res = await sendText({
        to: phone,
        body,
        leadId: job.leadId,
        dealId: job.dealId,
        contactName: job.contactName,
        user,
        source: job.ruleKey || "drip",
      });
      if (res.ok) {
        await resolveTextJob(job.id, {
          status: "sent",
          resolvedBy: user?.id || "",
          sentMessageId: res.message?.id || "",
        });
        if (res.queued) toast.info("Queued. It sends when quiet hours end.");
        return { ok: true };
      }
      toast.error(res.error || "Text failed to send");
      return { ok: false };
    } finally {
      setBusy((b) => {
        const next = { ...b };
        delete next[job.id];
        return next;
      });
    }
  };

  const skipJob = (job) =>
    resolveTextJob(job.id, { status: "skipped", resolvedBy: user?.id || "" });

  // Clear the whole queue without sending. Mirrors sendAll's confirm-first
  // shape; each job is marked skipped so the drip does not re-suggest it.
  const skipAll = async () => {
    const list = [...dueJobsVisible];
    if (!list.length) return;
    const sure = await confirm({
      title: "Skip all texts",
      body: `Skip all ${list.length} text${list.length === 1 ? "" : "s"}?`,
      note: "Nothing will be sent.",
      confirmLabel: "Skip all",
    });
    if (!sure) return;
    for (const job of list) {
      await skipJob(job);
    }
  };

  const sendAll = async () => {
    const list = [...dueJobsVisible];
    if (!list.length) return;
    const sure = await confirm({
      title: "Send All Texts",
      body: `Send ${list.length} text${list.length === 1 ? "" : "s"} now?`,
      note: "Each one is logged on its lead.",
      confirmLabel: "Send all",
    });
    if (!sure) return;
    let sent = 0;
    for (const job of list) {
      const r = await sendJob(job);
      if (r.ok) sent += 1;
    }
    toast.success(`Sent ${sent} of ${list.length}.`);
  };

  if (!allowed) {
    return (
      <div className="p-10 text-center text-slate-500">
        <MessageSquare className="mx-auto mb-2" />
        Texting isn't enabled for your role yet.
      </div>
    );
  }

  return (
    <div className="px-[25px] py-5">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-[24px] font-bold text-slate-900 flex items-center gap-2">
          <MessageSquare size={22} /> Texting
        </h1>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            Nothing sends without your approval
          </span>
          <button
            onClick={() => setNewTextOpen(true)}
            className="text-xs font-semibold text-white bg-themeColor rounded-lg px-3.5 py-2 hover:bg-themeColor/90"
          >
            ➕ New text
          </button>
          <button
            onClick={() => setGroupOpen(true)}
            className="text-xs font-semibold border border-slate-300 text-slate-700 rounded-lg px-3.5 py-2 hover:bg-slate-50"
          >
            👥 Group text
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 items-start">
        <div className="flex-[1.35] min-w-[340px] space-y-4">
          {/* Today's Texts */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
              <h2 className="text-[15px] font-bold text-slate-800">
                Today's Texts
              </h2>
              {Object.entries(ruleCounts).map(([k, n]) => (
                <span
                  key={k}
                  className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${(RULE_LABELS[k] || RULE_LABELS.drip).cls}`}
                >
                  {(RULE_LABELS[k] || { label: k }).label} · {n}
                </span>
              ))}
              {dueJobsVisible.length > 0 && (
                <div className="ml-auto flex items-center gap-2">
                  <button
                    onClick={skipAll}
                    className="text-xs font-semibold border border-slate-300 text-slate-500 rounded-lg px-3.5 py-2 hover:bg-slate-50"
                  >
                    Skip all
                  </button>
                  <button
                    onClick={sendAll}
                    className="text-xs font-semibold text-white bg-[#1E293B] rounded-lg px-3.5 py-2 hover:bg-slate-900"
                  >
                    Send all ({dueJobsVisible.length})
                  </button>
                </div>
              )}
            </div>
            {dueJobsVisible.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">
                🎉 Queue clear! New suggestions appear here as they come due.
              </p>
            ) : (
              dueJobsVisible.map((job) => (
                <div
                  key={job.id}
                  className="px-4 py-3 border-b border-slate-50 flex gap-3 items-start"
                >
                  <div className="min-w-[130px]">
                    <button
                      className="text-sm font-semibold text-slate-800 hover:text-themeColor text-left"
                      onClick={() =>
                        setSelectedKey(
                          job.leadId ? `lead:${job.leadId}` : `deal:${job.dealId}`,
                        )
                      }
                    >
                      {job.contactName || "Lead"}
                    </button>
                    <div
                      className={`inline-block ml-0 mt-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${(RULE_LABELS[job.ruleKey] || RULE_LABELS.drip).cls}`}
                    >
                      {(RULE_LABELS[job.ruleKey] || { label: job.ruleKey }).label}
                      {job.step ? ` · DAY ${job.step.replace("d", "")}` : ""}
                    </div>
                  </div>
                  <textarea
                    rows={2}
                    value={drafts[job.id] ?? job.body}
                    onChange={(e) =>
                      setDrafts((d) => ({ ...d, [job.id]: e.target.value }))
                    }
                    className="flex-1 text-[13px] text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:bg-white focus:border-themeColor outline-none"
                  />
                  <div className="flex flex-col gap-1.5">
                    <button
                      onClick={() => sendJob(job)}
                      disabled={!!busy[job.id]}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#1E293B] rounded-md px-3 py-1.5 disabled:opacity-50"
                    >
                      <Send size={11} /> {busy[job.id] ? "Sending…" : "Send"}
                    </button>
                    <button
                      onClick={() => skipJob(job)}
                      disabled={!!busy[job.id]}
                      className="inline-flex items-center gap-1 text-xs font-semibold border border-slate-300 text-slate-500 rounded-md px-3 py-1.5 hover:bg-slate-50"
                    >
                      <SkipForward size={11} /> Skip
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Inbox */}
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
              <h2 className="text-[15px] font-bold text-slate-800">Inbox</h2>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs font-semibold">
                <button
                  onClick={() => setInboxFilter("all")}
                  className={`px-3 py-1.5 ${inboxFilter === "all" ? "bg-[#1E293B] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  All
                </button>
                <button
                  onClick={() => setInboxFilter("unread")}
                  className={`px-3 py-1.5 ${inboxFilter === "unread" ? "bg-[#1E293B] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                >
                  Unread
                  {unread.leads && (
                    <>
                      {(() => {
                        const n =
                          Object.values(unread.leads).reduce((a, b) => a + b, 0) +
                          Object.values(unread.deals).reduce((a, b) => a + b, 0) +
                          unread.unmatched;
                        return n > 0 ? ` (${n})` : "";
                      })()}
                    </>
                  )}
                </button>
              </div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or number…"
                className="ml-auto text-sm border border-slate-200 rounded-lg px-3 py-1.5 w-56 outline-none focus:border-themeColor"
              />
            </div>
            {convos.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-400">
                No conversations yet. Texts you send from leads and deals all
                land here.
              </p>
            ) : (
              convos.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setSelectedKey(c.key)}
                  className={`w-full text-left px-4 py-3 border-b border-slate-50 flex items-center gap-3 hover:bg-slate-50 ${
                    selected?.key === c.key ? "bg-slate-50" : ""
                  }`}
                >
                  <div className="w-9 h-9 rounded-full bg-slate-100 text-linkColor font-bold text-xs flex items-center justify-center flex-shrink-0">
                    {avatarInitials(c.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <span
                        className={`text-sm truncate ${c.unread ? "font-bold text-slate-900" : "font-semibold text-slate-700"}`}
                      >
                        {c.name}
                      </span>
                      <span className="text-[11px] text-slate-400 whitespace-nowrap">
                        {fmtWhen(c.lastAt)}
                      </span>
                    </div>
                    <p
                      className={`text-xs truncate ${c.unread ? "font-semibold text-slate-700" : "text-slate-400"}`}
                    >
                      {c.lastBody}
                    </p>
                  </div>
                  {c.unread > 0 && (
                    <span className="min-w-[20px] h-5 rounded-full bg-themeColor text-white text-[11px] font-bold flex items-center justify-center px-1">
                      {c.unread}
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    title={pins[c.key] ? "Unpin" : "Pin to top"}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePin(c.key);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.stopPropagation();
                        togglePin(c.key);
                      }
                    }}
                    className={`text-sm px-1 ${pins[c.key] ? "" : "opacity-25 hover:opacity-70"}`}
                  >
                    📌
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Thread panel */}
        <div className="flex-1 min-w-[320px] sticky top-4">
          {selected ? (
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="bg-[#1E293B] text-white px-4 py-3">
                <div className="text-sm font-bold">{selected.name}</div>
                <div className="text-[11px] text-slate-300">
                  {fmtNum(selected.number)}
                </div>
              </div>
              <div className="p-3 h-[520px] flex flex-col">
                <TextThread
                  key={selected.key}
                  leadId={selected.leadId}
                  dealId={selected.dealId}
                  contactId={
                    selected.leadId
                      ? leadsById[selected.leadId]?.contactId ||
                        selected.contactId ||
                        ""
                      : selected.contactId || ""
                  }
                  contactName={selected.name}
                  phone={selected.number}
                />
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-sm text-slate-400">
              Pick a conversation to read and reply.
            </div>
          )}
        </div>
      </div>

      {groupOpen && (
        <GroupTextModal leads={leads} onClose={() => setGroupOpen(false)} />
      )}
      {newTextOpen && (
        <NewTextModal
          leads={leads}
          contacts={contactList}
          onPick={(convo) => {
            setDraftConvo(convo);
            setSelectedKey(convo.key);
          }}
          onClose={() => setNewTextOpen(false)}
        />
      )}
      {dialog}
    </div>
  );
}
